import { httpClient } from "@/lib/http";
import { LIVE_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/lib/http";
import type {
    ActiveDriversQueryParams,
    ActiveDriversResponse,
    ActiveTripsQueryParams,
    ActiveTripsResponse,
    LivePlatformStats,
    ClustersQueryParams,
    ClustersResponse,
    HeatmapQueryParams,
    HeatmapResponse,
    SupplyDemandQueryParams,
    SupplyDemandResponse,
    TrackedDriverDetails,
    TrackedTripDetails,
} from "../types";

// ============================================================================
// Helpers
// ============================================================================

const buildBoundingBoxQuery = (
    q: URLSearchParams,
    params: { minLat?: number; minLng?: number; maxLat?: number; maxLng?: number }
) => {
    if (params.minLat !== undefined) q.append("minLat", params.minLat.toString());
    if (params.minLng !== undefined) q.append("minLng", params.minLng.toString());
    if (params.maxLat !== undefined) q.append("maxLat", params.maxLat.toString());
    if (params.maxLng !== undefined) q.append("maxLng", params.maxLng.toString());
};

// ============================================================================
// Active Drivers API
// ============================================================================

export const getActiveDrivers = async (params: ActiveDriversQueryParams = {}) => {
    const q = new URLSearchParams();
    if (params.minutesThreshold !== undefined) q.append("minutesThreshold", params.minutesThreshold.toString());
    if (params.status) q.append("status", params.status);
    buildBoundingBoxQuery(q, params);
    const qs = q.toString();
    const url = qs ? `${LIVE_ENDPOINTS.DRIVERS}?${qs}` : LIVE_ENDPOINTS.DRIVERS;
    const { data } = await httpClient.get<ApiResponse<ActiveDriversResponse>>(url);
    return data;
};

export const trackDriver = async (driverId: string) => {
    const { data } = await httpClient.get<ApiResponse<TrackedDriverDetails>>(
        LIVE_ENDPOINTS.DRIVER_DETAILS(driverId)
    );
    return data;
};

// ============================================================================
// Active Trips API
// ============================================================================

export const getActiveTrips = async (params: ActiveTripsQueryParams = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.append("status", params.status);
    buildBoundingBoxQuery(q, params);
    const qs = q.toString();
    const url = qs ? `${LIVE_ENDPOINTS.TRIPS}?${qs}` : LIVE_ENDPOINTS.TRIPS;
    const { data } = await httpClient.get<ApiResponse<ActiveTripsResponse>>(url);
    return data;
};

export const trackTrip = async (tripId: string) => {
    const { data } = await httpClient.get<ApiResponse<TrackedTripDetails>>(
        LIVE_ENDPOINTS.TRIP_DETAILS(tripId)
    );
    return data;
};

// ============================================================================
// Live Stats API
// ============================================================================

export const getLivePlatformStats = async () => {
    const { data } = await httpClient.get<ApiResponse<LivePlatformStats>>(LIVE_ENDPOINTS.STATS);
    return data;
};

// ============================================================================
// Clusters API
// ============================================================================

export const getRideClusters = async (params: ClustersQueryParams = {}) => {
    const q = new URLSearchParams();
    if (params.hoursBack !== undefined) q.append("hoursBack", params.hoursBack.toString());
    if (params.clusterType) q.append("clusterType", params.clusterType);
    buildBoundingBoxQuery(q, params);
    const qs = q.toString();
    const url = qs ? `${LIVE_ENDPOINTS.CLUSTERS}?${qs}` : LIVE_ENDPOINTS.CLUSTERS;
    const { data } = await httpClient.get<ApiResponse<ClustersResponse>>(url);
    return data;
};

// ============================================================================
// Heatmap API
// ============================================================================

export const getDemandHeatmap = async (params: HeatmapQueryParams = {}) => {
    const q = new URLSearchParams();
    if (params.hoursBack !== undefined) q.append("hoursBack", params.hoursBack.toString());
    if (params.dataSource) q.append("dataSource", params.dataSource);
    buildBoundingBoxQuery(q, params);
    const qs = q.toString();
    const url = qs ? `${LIVE_ENDPOINTS.HEATMAP}?${qs}` : LIVE_ENDPOINTS.HEATMAP;
    const { data } = await httpClient.get<ApiResponse<HeatmapResponse>>(url);
    return data;
};

// ============================================================================
// Supply-Demand API
// ============================================================================

export const getSupplyDemandAnalysis = async (params: SupplyDemandQueryParams = {}) => {
    const q = new URLSearchParams();
    if (params.hoursBack !== undefined) q.append("hoursBack", params.hoursBack.toString());
    buildBoundingBoxQuery(q, params);
    const qs = q.toString();
    const url = qs ? `${LIVE_ENDPOINTS.SUPPLY_DEMAND}?${qs}` : LIVE_ENDPOINTS.SUPPLY_DEMAND;
    const { data } = await httpClient.get<ApiResponse<SupplyDemandResponse>>(url);
    return data;
};
